import React, { useState, useEffect } from "react";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useMutation } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ThumbsUp, Meh, AlertCircle, Star, VolumeX } from "lucide-react";
import { toast } from "sonner";

export default function FeedbackPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { track, listenPercentage, listenDuration } = location.state || {};
  
  const [currentUser, setCurrentUser] = useState(null);
  const [content, setContent] = useState("");
  const [sentiment, setSentiment] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [productionRating, setProductionRating] = useState(0);
  const [vocalsRating, setVocalsRating] = useState(0);
  const [lyricsRating, setLyricsRating] = useState(0);
  const [originalityRating, setOriginalityRating] = useState(0);
  const [submitError, setSubmitError] = useState("");
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    loadUser();
    
    // Check if audio is muted
    const checkMute = () => {
      const audio = document.querySelector('audio');
      const video = document.querySelector('video');
      const media = audio || video;
      if (media && media.muted) {
        setIsMuted(true);
      }
    };
    
    checkMute();
    const interval = setInterval(checkMute, 1000);
    return () => clearInterval(interval);
  }, []);

  const loadUser = async () => {
    try {
      // Get session from digitalOcean client
      const { data: sessionData, error: sessionError } = await digitalOcean.auth.getSession();
      
      if (sessionError || !sessionData?.session) {
        navigate(createPageUrl('Auth'));
        return;
      }

      const { data: profile, error: profileError } = await digitalOcean
        .from('profiles')
        .select('*')
        .eq('id', sessionData.session.user.id)
        .single();

      if (profileError) {
        console.error("Error loading profile:", profileError);
        navigate(createPageUrl('Auth'));
        return;
      }

      setCurrentUser(profile);
    } catch (error) {
      console.error("Error loading user:", error);
      navigate(createPageUrl('Auth'));
    }
  };

  const availableTags = [
    "production", "mixing", "mastering", "vocals", "lyrics", 
    "melody", "rhythm", "energy", "originality", "structure"
  ];

  const submitFeedbackMutation = useMutation({
    mutationFn: async () => {
      // Validation
      if (!sentiment) {
        throw new Error("Please select a sentiment");
      }

      if (!productionRating || !vocalsRating || !lyricsRating || !originalityRating) {
        throw new Error("Please provide all ratings");
      }

      // Allow feedback if listened to 75% OR 3 minutes (180 seconds)
      const hasListenedEnough = listenPercentage >= 75 || (listenDuration && listenDuration >= 180);
      if (!hasListenedEnough) {
        throw new Error("You must listen to at least 75% of the track or 3 minutes to give feedback");
      }

      // Calculate overall rating
      const overallRating = (productionRating + vocalsRating + lyricsRating + originalityRating) / 4;

      // Calculate word count and tier
      const wordCount = content.trim().split(/\s+/).length;
      let tier = "basic";
      let basePoints = 10;

      if (wordCount >= 200) {
        tier = "masterful";
        basePoints = 50;
      } else if (wordCount >= 100) {
        tier = "comprehensive";
        basePoints = 30;
      } else if (wordCount >= 50) {
        tier = "detailed";
        basePoints = 20;
      }

      // Create feedback entity
      if (!track?.id || !currentUser?.id) {
        throw new Error("Missing required data: track or user information");
      }
      
      const { error: feedbackError } = await digitalOcean
        .from('feedback')
        .insert({
          track_id: track.id,
          reviewer_id: currentUser.id,
          feedback_type: sentiment,
          comment: content
        });

      if (feedbackError) throw feedbackError;

      // Update track stats
      const updateField = `${sentiment}_count`;
      const { error: trackError } = await digitalOcean
        .from('tracks')
        .update({
          [updateField]: (track[updateField] || 0) + 1
        })
        .eq('id', track.id);

      if (trackError) throw trackError;

      // Update user balance (credits)
      const { error: balanceError } = await digitalOcean
        .from('profiles')
        .update({
          balance: (currentUser.balance || 0) + basePoints
        })
        .eq('id', currentUser.id);

      if (balanceError) throw balanceError;

      // Create archive log
      await digitalOcean
        .from('archive_logs')
        .insert({
          user_id: currentUser.id,
          action: 'feedback_given',
          details: {
            track_title: track.title,
            artist_name: track.artist_name,
            sentiment,
            tier,
            points_earned: basePoints
          }
        });

      // Create notification for track owner
      await digitalOcean
        .from('notifications')
        .insert({
          user_id: track.artist_id,
          title: 'New Feedback Received',
          message: `You received ${sentiment} feedback on "${track.title}"`,
          is_read: false
        });
    },
    onSuccess: () => {
      toast.success("Feedback submitted successfully!");
      navigate(createPageUrl("Discover"));
    },
    onError: (error) => {
      setSubmitError(error.message || "Failed to submit feedback. Please try again.");
      toast.error(error.message);
    }
  });

  if (!track) {
    navigate(createPageUrl("Discover"));
    return null;
  }

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#707070]" />
      </div>
    );
  }

  const canSubmit = sentiment && productionRating && vocalsRating && lyricsRating && originalityRating && (listenPercentage >= 75 || (listenDuration && listenDuration >= 180));

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Mute Warning */}
        {isMuted && (
          <div className="neuro-base rounded-3xl p-4 mb-6 bg-[#2a1a1a] border-2 border-[#b09090]">
            <div className="flex items-center gap-3">
              <VolumeX className="w-6 h-6 text-[#b09090]" />
              <div>
                <p className="text-sm font-medium text-[#b09090]">Audio is Muted</p>
                <p className="text-xs text-[#808080] mt-1">
                  Please unmute to properly listen to the track before giving feedback
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Track Preview */}
        <div className="neuro-base rounded-3xl p-6 mb-6">
          <div className="flex gap-4 mb-4">
            <img
              src={track.cover_image_url}
              alt={track.title}
              className="w-20 h-20 rounded-xl object-cover neuro-pressed"
            />
            <div>
              <h2 className="text-xl font-light text-[#d0d0d0] mb-1">{track.title}</h2>
              <p className="text-sm text-[#808080]">{track.artist_name}</p>
              <p className="text-xs text-[#707070] mt-2">
                Listened: {Math.round(listenPercentage)}%
              </p>
            </div>
          </div>
        </div>

        {submitError && (
          <div className="neuro-pressed rounded-2xl p-4 mb-6 bg-[#1a0f0f]">
            <p className="text-sm text-[#d09090] text-center">{submitError}</p>
          </div>
        )}

        {/* Sentiment Selection */}
        <div className="neuro-base rounded-3xl p-6 mb-6">
          <h3 className="text-sm text-[#808080] mb-4">Overall Sentiment</h3>
          <div className="grid grid-cols-3 gap-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSentiment("praise")}
                    className={`neuro-flat rounded-2xl p-4 flex flex-col items-center gap-2 smooth-transition hover:scale-105 ${
                      sentiment === "praise" ? "neuro-pressed" : ""
                    }`}
                  >
                    <ThumbsUp className={`w-6 h-6 ${sentiment === "praise" ? "text-[#90b090]" : "text-[#707070]"}`} />
                    <span className={`text-sm ${sentiment === "praise" ? "text-[#90b090]" : "text-[#808080]"}`}>
                      Praise
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Mark this feedback as positive praise</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSentiment("neutral")}
                    className={`neuro-flat rounded-2xl p-4 flex flex-col items-center gap-2 smooth-transition hover:scale-105 ${
                      sentiment === "neutral" ? "neuro-pressed" : ""
                    }`}
                  >
                    <Meh className={`w-6 h-6 ${sentiment === "neutral" ? "text-[#909090]" : "text-[#707070]"}`} />
                    <span className={`text-sm ${sentiment === "neutral" ? "text-[#909090]" : "text-[#808080]"}`}>
                      Neutral
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Mark this feedback as neutral</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSentiment("scathe")}
                    className={`neuro-flat rounded-2xl p-4 flex flex-col items-center gap-2 smooth-transition hover:scale-105 ${
                      sentiment === "scathe" ? "neuro-pressed" : ""
                    }`}
                  >
                    <AlertCircle className={`w-6 h-6 ${sentiment === "scathe" ? "text-[#b09090]" : "text-[#707070]"}`} />
                    <span className={`text-sm ${sentiment === "scathe" ? "text-[#b09090]" : "text-[#808080]"}`}>
                      Scathe
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Mark this feedback as scathing criticism</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Ratings */}
        <div className="neuro-base rounded-3xl p-6 mb-6">
          <h3 className="text-sm text-[#808080] mb-4">Rate This Track</h3>
          
          <div className="space-y-4">
            {[
              { label: "Production", value: productionRating, setter: setProductionRating },
              { label: "Vocals", value: vocalsRating, setter: setVocalsRating },
              { label: "Lyrics", value: lyricsRating, setter: setLyricsRating },
              { label: "Originality", value: originalityRating, setter: setOriginalityRating }
            ].map(({ label, value, setter }) => (
              <div key={label}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#a0a0a0]">{label}</span>
                  <span className="text-sm text-[#707070]">{value}/5</span>
                </div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <TooltipProvider key={rating}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setter(rating)}
                            className="flex-1 neuro-flat rounded-xl py-3 smooth-transition hover:scale-105"
                          >
                            <Star
                              className={`w-6 h-6 mx-auto ${
                                rating <= value ? "text-[#d4af37] fill-[#d4af37]" : "text-[#404040]"
                              }`}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Rate {label} {rating} out of 5</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tags */}
        <div className="neuro-base rounded-3xl p-6 mb-6">
          <h3 className="text-sm text-[#808080] mb-4">Focus Areas (Optional)</h3>
          <div className="flex flex-wrap gap-2">
            {availableTags.map(tag => (
              <TooltipProvider key={tag}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        if (selectedTags.includes(tag)) {
                          setSelectedTags(selectedTags.filter(t => t !== tag));
                        } else {
                          setSelectedTags([...selectedTags, tag]);
                        }
                      }}
                      className={`neuro-base rounded-full px-4 py-2 text-xs smooth-transition hover:scale-105 ${
                        selectedTags.includes(tag) ? "neuro-pressed text-[#a0a0a0]" : "text-[#707070]"
                      }`}
                    >
                      {tag}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{selectedTags.includes(tag) ? `Remove ${tag} tag` : `Add ${tag} tag`}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>

        {/* Written Feedback */}
        <div className="neuro-base rounded-3xl p-6 mb-6">
          <h3 className="text-sm text-[#808080] mb-4">
            Written Feedback (Optional, but earns more credits)
          </h3>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share your thoughts on the track..."
            className="bg-[#0a0a0a] border-none text-[#d0d0d0] placeholder:text-[#505050] neuro-pressed rounded-2xl min-h-[150px] resize-none mb-2"
          />
          <p className="text-xs text-[#707070]">
            {content.trim().split(/\s+/).filter(w => w).length} words
          </p>
        </div>

        {/* Reward Tiers Info */}
        <div className="neuro-pressed rounded-2xl p-4 mb-6">
          <p className="text-xs text-[#808080] mb-2">Feedback Tiers & Rewards:</p>
          <div className="space-y-1 text-xs text-[#707070]">
            <p>• Basic (ratings only): 10 credits</p>
            <p>• Detailed (50+ words): 20 credits</p>
            <p>• Comprehensive (100+ words): 30 credits</p>
            <p>• Masterful (200+ words): 50 credits</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            onClick={() => navigate(createPageUrl("Discover"))}
            className="flex-1 neuro-flat rounded-2xl py-4"
            tooltip="Go back to discovering tracks without submitting feedback"
          >
            <span className="text-[#808080]">Cancel</span>
          </Button>
          <Button
            onClick={() => submitFeedbackMutation.mutate()}
            disabled={!canSubmit || submitFeedbackMutation.isPending}
            className={`flex-1 rounded-2xl py-4 ${
              canSubmit && !submitFeedbackMutation.isPending
                ? "neuro-base active:neuro-pressed hover:scale-[1.02] text-[#b0b0b0]"
                : "neuro-pressed opacity-40 cursor-not-allowed text-[#606060]"
            }`}
            tooltip={!canSubmit ? "Complete all required fields to submit feedback" : "Submit your feedback and earn credits"}
          >
            {submitFeedbackMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              "Submit Feedback"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
